import httpClient from '../httpClient';

export interface IntegrationStatus {
    voip: boolean;
    voip_provider?: string | null; // e.g. 'twilio' | 'arestik' | 'voiceflow_c2c' – arestik and voiceflow_c2c require user extension
    email: boolean;
    whatsapp: boolean;
    sms: boolean;
    payment: boolean;
    /** Meta app credentials saved for the Facebook Lead Ads campaign source. */
    facebook: boolean;
}

export type IntegrationType = 'voip' | 'email' | 'whatsapp' | 'sms' | 'payment' | 'facebook';

export interface TenantIntegration {
    id: number;
    tenant_id?: string;
    integration_type: IntegrationType;
    provider_name: string;
    is_active: boolean;
    /** Secrets are masked by the API (`••••1234`); see `isMaskedSecret`. */
    configuration: Record<string, any>;
    /** Last credentials-only check (`test-connection`); never sends anything. */
    last_checked_at?: string | null;
    last_check_ok?: boolean | null;
    last_check_message?: string | null;
    /** Last real send (`test`): an SMS, email, call or payment actually went out. */
    last_tested_at?: string | null;
    last_test_ok?: boolean | null;
    last_test_message?: string | null;
    created_at: string;
    updated_at: string;
}

/**
 * Normalised `data` of `POST /integrations/manage/{id}/test-connection`.
 * `valid: null` + `supported: false` means the provider has no credentials-only check.
 */
export interface ConnectionCheckResult extends FacebookConnectionTestResult {
    supported?: boolean;
    provider?: string;
}

/**
 * `data` of `POST /integrations/manage/{id}/test-connection` for a `facebook` integration.
 * `success` / `error` are the legacy app-credentials-only shape, kept as a fallback.
 */
export interface FacebookConnectionTestResult {
    /** `null` when the provider has no credentials-only check. */
    valid?: boolean | null;
    token_type?: string | null;
    /** Unix seconds (Meta `debug_token`), ISO string, or null / 0 when it never expires. */
    expires_at?: number | string | null;
    granted_scopes?: string[];
    missing_required?: string[];
    missing_recommended?: string[];
    message?: string;
    success?: boolean;
    error?: string;
}

export interface IntegrationWebhook {
    id: number;
    tenant_id: string;
    integration_type: string;
    webhook_url: string;
    webhook_secret: string | null;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export interface CallRequest {
    phone_number: string;
    customer_id?: string;
}

export interface EmailRequest {
    to: string;
    template: string;
    data?: Record<string, any>;
}

export interface MessageRequest {
    phone_number: string;
    message: string;
}

export interface PaymentRequest {
    amount: number;
    currency: string;
    order_id: string;
}

export interface IntegrationResponse {
    success: boolean;
    data?: any;
    message?: string;
}

class IntegrationService {
    /**
     * Get active integrations for current tenant (backend uses authenticated user's tenant context).
     */
    async getActiveIntegrations(): Promise<IntegrationStatus> {
        const response = await httpClient.get<IntegrationResponse>('/integrations/active');
        return response.data.data;
    }

    /**
     * Get integration status for current tenant (backend uses authenticated user's tenant context).
     */
    async getIntegrationStatus(): Promise<Record<string, any>> {
        const response = await httpClient.get<IntegrationResponse>('/integrations/status');
        return response.data.data;
    }

    /**
     * Initiate VOIP call
     */
    async initiateCall(request: CallRequest): Promise<IntegrationResponse> {
        const response = await httpClient.post<IntegrationResponse>(
            '/integrations/voip/call',
            request
        );
        return response.data;
    }

    /**
     * Send email
     */
    async sendEmail(request: EmailRequest): Promise<IntegrationResponse> {
        const response = await httpClient.post<IntegrationResponse>(
            '/integrations/email/send',
            request
        );
        return response.data;
    }

    /**
     * Send WhatsApp message
     */
    async sendWhatsApp(request: MessageRequest): Promise<IntegrationResponse> {
        const response = await httpClient.post<IntegrationResponse>(
            '/integrations/whatsapp/send',
            request
        );
        return response.data;
    }

    /**
     * Send SMS
     */
    async sendSMS(request: MessageRequest): Promise<IntegrationResponse> {
        const response = await httpClient.post<IntegrationResponse>(
            '/integrations/sms/send',
            request
        );
        return response.data;
    }

    /**
     * Create payment
     */
    async createPayment(request: PaymentRequest): Promise<IntegrationResponse> {
        const response = await httpClient.post<IntegrationResponse>(
            '/integrations/payment/create',
            request
        );
        return response.data;
    }

    // Management API methods

    /**
     * Get all integrations for management
     */
    async getIntegrations(): Promise<TenantIntegration[]> {
        const response = await httpClient.get<IntegrationResponse>('/integrations/manage');
        return response.data.data;
    }

    /**
     * Get specific integration
     */
    async getIntegration(id: number): Promise<TenantIntegration> {
        const response = await httpClient.get<IntegrationResponse>(`/integrations/manage/${id}`);
        return response.data.data;
    }

    /**
     * Create new integration
     */
    async createIntegration(data: {
        integration_type: string;
        provider_name: string;
        configuration?: Record<string, any>;
    }): Promise<TenantIntegration> {
        const response = await httpClient.post<IntegrationResponse>('/integrations/manage', data);
        return response.data.data;
    }

    /**
     * Update integration
     */
    async updateIntegration(
        id: number,
        data: Partial<Pick<TenantIntegration, 'provider_name' | 'configuration' | 'is_active'>>
    ): Promise<TenantIntegration> {
        const response = await httpClient.put<IntegrationResponse>(
            `/integrations/manage/${id}`,
            data
        );
        return response.data.data;
    }

    /**
     * Delete integration
     */
    async deleteIntegration(id: number): Promise<void> {
        await httpClient.delete(`/integrations/manage/${id}`);
    }

    /**
     * Test integration
     */
    async testIntegration(id: number, testData: Record<string, any>): Promise<any> {
        const response = await httpClient.post(`/integrations/manage/${id}/test`, {
            test_data: testData,
        });
        return response.data;
    }

    /**
     * Test connection
     */
    async testConnection(id: number): Promise<any> {
        const response = await httpClient.post(`/integrations/manage/${id}/test-connection`);
        return response.data;
    }

    /**
     * Credentials-only check (sends nothing). Flattens the envelope so callers get
     * `{ valid, supported, message, ...providerExtras }`; the legacy Facebook
     * `success` / `error` shape is mapped onto `valid` / `message`.
     */
    async checkConnection(id: number): Promise<ConnectionCheckResult> {
        const body = await this.testConnection(id);
        const data = (body?.data ?? {}) as ConnectionCheckResult;
        return {
            ...data,
            valid: data.valid !== undefined ? data.valid : (data.success ?? body?.success ?? null),
            supported: data.supported ?? true,
            message: data.message ?? data.error ?? body?.message,
        };
    }

    /**
     * Activate integration
     */
    async activateIntegration(id: number): Promise<TenantIntegration> {
        const response = await httpClient.post<IntegrationResponse>(
            `/integrations/manage/${id}/activate`
        );
        return response.data.data;
    }

    /**
     * Deactivate integration
     */
    async deactivateIntegration(id: number): Promise<TenantIntegration> {
        const response = await httpClient.post<IntegrationResponse>(
            `/integrations/manage/${id}/deactivate`
        );
        return response.data.data;
    }

    /**
     * Get webhooks for integration
     */
    async getIntegrationWebhooks(integrationId: number): Promise<IntegrationWebhook[]> {
        const response = await httpClient.get<IntegrationResponse>(
            `/integrations/manage/${integrationId}/webhooks`
        );
        return response.data.data;
    }

    /**
     * Create webhook for integration
     */
    async createWebhook(
        integrationId: number,
        data: {
            webhook_url: string;
            webhook_secret?: string;
        }
    ): Promise<IntegrationWebhook> {
        const response = await httpClient.post<IntegrationResponse>(
            `/integrations/manage/${integrationId}/webhooks`,
            data
        );
        return response.data.data;
    }

    /**
     * Get available providers for integration type
     */
    async getProviders(type: string): Promise<string[]> {
        const response = await httpClient.get<IntegrationResponse>(
            `/integrations/manage/providers?type=${type}`
        );
        return response.data.data;
    }

    /**
     * Check if specific integration type is enabled (uses backend tenant context).
     */
    async isIntegrationEnabled(type: keyof IntegrationStatus): Promise<boolean> {
        try {
            const status = await this.getActiveIntegrations();
            return status[type];
        } catch (error) {
            console.error(`Error checking ${type} integration status:`, error);
            return false;
        }
    }

    /**
     * Check if click-to-call is enabled
     */
    async isClickToCallEnabled(): Promise<boolean> {
        return this.isIntegrationEnabled('voip');
    }

    /**
     * Check if email integration is enabled
     */
    async isEmailEnabled(): Promise<boolean> {
        return this.isIntegrationEnabled('email');
    }

    /**
     * Check if WhatsApp integration is enabled
     */
    async isWhatsAppEnabled(): Promise<boolean> {
        return this.isIntegrationEnabled('whatsapp');
    }

    /**
     * Check if SMS integration is enabled
     */
    async isSMSEnabled(): Promise<boolean> {
        return this.isIntegrationEnabled('sms');
    }

    /**
     * Check if payment integration is enabled
     */
    async isPaymentEnabled(): Promise<boolean> {
        return this.isIntegrationEnabled('payment');
    }
}

export const integrationService = new IntegrationService();
